import type {
  Action,
  ActionConfig,
  ActionRegistry,
  GenericDisplayOptions,
} from "@core/features/actions";
import {
  isActionAvailable,
  isActionCompleted,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
  TARGET_OBJECT_TYPE,
} from "@core/features/actions";

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get CTA URL from display_options.cta.url
 */
function getCtaUrl(action: Action): string | undefined {
  const opts = action.display_options as GenericDisplayOptions | null;
  return opts?.cta?.url;
}

/**
 * Check if CTA URL is external (opens in new tab).
 */
function isCtaExternal(action: Action): boolean {
  const opts = action.display_options as GenericDisplayOptions | null;
  return opts?.cta?.isExternal ?? false;
}

// =============================================================================
// URL Variable Interpolation
// =============================================================================

type UrlVariables = {
  programId?: number;
  orgId?: number | null;
  quizId?: number | null;
};

/**
 * Interpolate URL variables in a CTA URL template.
 * Supported variables: {programId}, {orgId}, {quizId}
 */
function interpolateCtaUrl(url: string, variables: UrlVariables): string {
  return url
    .replace(/{programId}/g, String(variables.programId ?? ""))
    .replace(/{orgId}/g, String(variables.orgId ?? ""))
    .replace(/{quizId}/g, String(variables.quizId ?? ""));
}

/**
 * Extract URL variables from an action for interpolation.
 */
function getUrlVariables(action: Action): UrlVariables {
  const targetData = action.target_object_data as Record<string, unknown> | null;

  return {
    programId: action.program,
    orgId: (targetData?.organization_id as number | null) ?? null,
    quizId: action.target_object_type === "quiz" ? action.target_object_id : null,
  };
}

/**
 * Navigate to CTA URL, interpolating variables and handling external URLs.
 * - URL templates can include {programId}, {orgId}, {quizId}
 * - External URLs (isExternal: true) open in a new tab
 */
function navigateCtaUrl(
  action: Action,
  navigate: (path: string) => void
): void {
  const url = getCtaUrl(action);
  if (!url) return;

  const variables = getUrlVariables(action);
  const interpolatedUrl = interpolateCtaUrl(url, variables);

  if (isCtaExternal(action)) {
    window.open(interpolatedUrl, "_blank");
  } else {
    navigate(interpolatedUrl);
  }
}

// =============================================================================
// Platform Action Registry
// =============================================================================

/**
 * Platform action registry.
 * Defines onClick behavior for each action type.
 */
export const actionRegistry: ActionRegistry = [
  // ============================================
  // Actions WITH navigation
  // ============================================
  {
    slug: ACTION_TYPE_SLUG.PROFILE_PARTICIPANT,
    onClick: (action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.IN_PROGRESS);
      navigate(`/program/${action.program}/profile`);
    },
    navigation: {
      title: "Profile",
      href: (programId) => `/program/${programId}/profile`,
      routePattern: "/program/:id/profile",
    },
  },
  {
    slug: ACTION_TYPE_SLUG.SCHEDULE,
    onClick: (action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.IN_PROGRESS);
      navigate(`/program/${action.program}/schedule`);
    },
    navigation: {
      title: "Schedule",
      href: (programId) => `/program/${programId}/schedule`,
      routePattern: "/program/:id/schedule",
    },
  },
  {
    slug: ACTION_TYPE_SLUG.SEND_INVITATION,
    onClick: (action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.IN_PROGRESS);
      // Organization invitations go to org admin coworkers page
      if (action.target_object_type === TARGET_OBJECT_TYPE.ORGANIZATION) {
        const orgId = (action.target_object_data as Record<string, unknown>)?.id;
        if (orgId) {
          navigate(`/program/${action.program}/organization/${orgId}/admin/coworkers`);
          return;
        }
      }
      // Default: program invitations
      navigate(`/program/${action.program}/invitations`);
    },
    navigation: {
      title: "Invitations",
      href: (programId) => `/program/${programId}/invitations`,
      routePattern: "/program/:id/invitations",
      getPageState: (action) => ({
        isReadOnly: !isActionAvailable(action),
      }),
    },
  },
  {
    slug: ACTION_TYPE_SLUG.WORKBOOK_DOWNLOAD,
    onClick: (action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.COMPLETED);
      navigateCtaUrl(action, navigate);
    },
    navigation: {
      title: "Resources",
      href: (programId) => `/program/${programId}/resources`,
      routePattern: "/program/:id/resources",
    },
  },
  {
    slug: ACTION_TYPE_SLUG.SOCIALMEDIAKIT_DOWNLOAD,
    onClick: (action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.COMPLETED);
      navigateCtaUrl(action, navigate);
    },
    navigation: {
      title: "Resources",
      href: (programId) => `/program/${programId}/resources`,
      routePattern: "/program/:id/resources",
    },
  },

  {
    slug: ACTION_TYPE_SLUG.MATCHING_QUIZ,
    onClick: (action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.IN_PROGRESS);
      navigate(`/program/${action.program}/profile/matching`);
    },
    navigation: {
      title: "Matching Quiz",
      href: (programId) => `/program/${programId}/profile/matching`,
      routePattern: "/program/:id/profile/matching",
    },
  },

  // ============================================
  // Actions WITHOUT navigation (phase action only)
  // ============================================
  {
    slug: ACTION_TYPE_SLUG.MEETING_SURVEY,
    onClick: (action, { openDialog, updateStatus }) => {
      updateStatus(ACTION_STATUS.IN_PROGRESS);
      openDialog("quiz", {
        quizId: action.target_object_id,
        userActionId: action.id,
        actionCompleted: isActionCompleted(action),
      });
    },
  },
  {
    slug: ACTION_TYPE_SLUG.MEETING,
    onClick: (action, { navigate }) => {
      navigate(`/video/join/${action.target_object_id}`);
    },
    navigation: {
      title: "Video",
      href: (_programId) => `/video/join`,
      routePattern: "/video/join/:meetingId?",
    },
  },
  {
    slug: ACTION_TYPE_SLUG.MEETING_STANDALONE,
    onClick: (action, { navigate }) => {
      const bindingId =
        action.binding_object_id ??
        (action.binding_object_data as Record<string, unknown>)?.id;
      navigate(`/video/join/${bindingId}`);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.MEETING_READING,
    onClick: (action, { openSheet, updateStatus }) => {
      const slug = (action.target_object_data as { slug?: string })?.slug;
      if (slug) openSheet(slug);
      updateStatus(ACTION_STATUS.COMPLETED);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.MEETING_SCRIPT,
    onClick: (action, { openSheet, updateStatus }) => {
      const slug = (action.target_object_data as { slug?: string })?.slug;
      if (slug) openSheet(slug);
      updateStatus(ACTION_STATUS.COMPLETED);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.ARTICLE_INFORMATIONAL,
    onClick: (action, { openSheet, updateStatus }) => {
      const slug = (action.target_object_data as { slug?: string })?.slug;
      if (slug) openSheet(slug);
      updateStatus(ACTION_STATUS.COMPLETED);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.ORIENTATION_RSVP,
    onClick: (action, { navigate, openSheet, updateStatus }) => {
      updateStatus(ACTION_STATUS.COMPLETED);
      const slug = (action.target_object_data as { slug?: string })?.slug;
      if (slug) {
        openSheet(slug);
      } else {
        navigateCtaUrl(action, navigate);
      }
    },
  },
  {
    slug: ACTION_TYPE_SLUG.CALENDAR_ADD,
    onClick: (_action, { updateStatus }) => {
      // Mark as completed when user interacts with calendar
      updateStatus(ACTION_STATUS.COMPLETED);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.UPDATE_TIMEZONE,
    onClick: (_action, { navigate, updateStatus }) => {
      updateStatus(ACTION_STATUS.IN_PROGRESS);
      navigate("/account");
    },
  },

  // ============================================
  // Organization Admin Actions
  // ============================================
  {
    slug: ACTION_TYPE_SLUG.ORG_MANAGE,
    onClick: (action, { navigate }) => {
      const orgId = (action.target_object_data as Record<string, unknown>)?.id;
      if (orgId) navigate(`/program/${action.program}/organization/${orgId}/admin`);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.ORG_UPDATE_SOCIALS,
    onClick: (action, { navigate }) => {
      const orgId = (action.target_object_data as Record<string, unknown>)?.id;
      if (orgId) navigate(`/program/${action.program}/organization/${orgId}/admin/profile`);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.ORG_UPLOAD_LOGO,
    onClick: (action, { navigate }) => {
      const orgId = (action.target_object_data as Record<string, unknown>)?.id;
      if (orgId) navigate(`/program/${action.program}/organization/${orgId}/admin/profile`);
    },
  },
  {
    slug: ACTION_TYPE_SLUG.ORG_DISPLAY_NAME,
    onClick: (action, { navigate }) => {
      const orgId = (action.target_object_data as Record<string, unknown>)?.id;
      if (orgId) navigate(`/program/${action.program}/organization/${orgId}/admin/profile`);
    },
  },
] satisfies ActionConfig[];
